import { useEffect, useRef } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import ProtectedRoute from './auth/ProtectedRoute.jsx';
import AppShell from './components/AppShell.jsx';
import LoginPage from './pages/LoginPage.jsx';
import AcceptInvitePage from './pages/AcceptInvitePage.jsx';
import ForgotPasswordPage from './pages/ForgotPasswordPage.jsx';
import ResetPasswordPage from './pages/ResetPasswordPage.jsx';
import NoProfilePage from './pages/NoProfilePage.jsx';
import DashboardPage from './pages/DashboardPage.jsx';
import SectionsPage from './pages/SectionsPage.jsx';
import SectionBuilderPage from './pages/SectionBuilderPage.jsx';
import EnrollmentsPage from './pages/EnrollmentsPage.jsx';
import CheckInPage, { RememberMePage } from './pages/CheckInPage.jsx';
import MyRecordPage from './pages/MyRecordPage.jsx';
import TrackerPage from './pages/TrackerPage.jsx';
import CompliancePage from './pages/CompliancePage.jsx';
import TranscriptPage from './pages/TranscriptPage.jsx';
import PacketPage from './pages/PacketPage.jsx';
import CatalogPage from './pages/CatalogPage.jsx';
import StaffPage from './pages/StaffPage.jsx';
import SessionsPage from './pages/SessionsPage.jsx';
import SessionRosterPage from './pages/SessionRosterPage.jsx';
import PresenterPage from './pages/PresenterPage.jsx';
import AttendanceRecordPage from './pages/AttendanceRecordPage.jsx';
import AffiliationsPage from './pages/AffiliationsPage.jsx';
import SkillSheetsPage from './pages/SkillSheetsPage.jsx';
import PersonnelPage from './pages/PersonnelPage.jsx';
import PeopleReviewPage from './pages/PeopleReviewPage.jsx';
import AffiliationAnswerPage from './pages/AffiliationAnswerPage.jsx';

/**
 * Move focus to the top of the document on navigation.
 *
 * A single-page app changes the content without changing the page, so a screen
 * reader announces nothing and a keyboard user's focus is left wherever the old
 * page put it. This restores the behaviour a normal page load would have had
 * (Decision #18).
 */
function FocusOnRouteChange() {
  const location = useLocation();
  const first = useRef(true);

  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    const target = document.querySelector('main') ?? document.body;
    target.focus?.();
  }, [location.pathname]);

  return null;
}

export function App() {
  return (
    <>
      <FocusOnRouteChange />
      <Routes>
        {/* PARTICIPANT-FACING and deliberately public. This is where the session
            QR code lands; participants have no staff account (Decision #4),
            so it must never sit behind ProtectedRoute. */}
        <Route path="/checkin" element={<CheckInPage />} />
        {/* The read-aloud / paste-into-chat door. Same page, same flow. */}
        <Route path="/j/:code" element={<CheckInPage />} />
        {/* Where the personal-link email lands (M25, Decision #64): stores the
            key on this phone and says so. Public for the same reason. */}
        <Route path="/me/:key" element={<RememberMePage />} />
        {/* The participant's own completion record (Scene 11), signed in by
            the same key. Public route; the key decides what it shows. */}
        <Route path="/me" element={<MyRecordPage />} />
        <Route path="/me/transcript" element={<TranscriptPage participant />} />

        {/* An agency representative answering "is this person with you?"
            (M43). Public: they have no account; the one-time link is the
            credential, and only a tap on the page records an answer. */}
        <Route path="/affiliation/:token" element={<AffiliationAnswerPage />} />

        {/* Unauthenticated */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/accept-invite" element={<AcceptInvitePage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        {/* Signed in but not provisioned — deliberately outside ProtectedRoute,
            which would bounce straight back here and loop. */}
        <Route path="/no-profile" element={<NoProfilePage />} />

        <Route
          path="/"
          element={
            <ProtectedRoute>
              <AppShell>
                <DashboardPage />
              </AppShell>
            </ProtectedRoute>
          }
        />

        {/* Course sections (H2 v2, Path A). /sections/new and /sections/:id are
            the same builder — an unsaved section and a saved one differ only in
            whether there is a row behind them yet. */}
        <Route
          path="/sections"
          element={
            <ProtectedRoute>
              <AppShell>
                <SectionsPage />
              </AppShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/personnel"
          element={
            <ProtectedRoute>
              <AppShell>
                <PersonnelPage />
              </AppShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/people-review"
          element={
            <ProtectedRoute>
              <AppShell>
                <PeopleReviewPage />
              </AppShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/skill-sheets"
          element={
            <ProtectedRoute>
              <AppShell>
                <SkillSheetsPage />
              </AppShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/people/:rosterId/affiliations"
          element={
            <ProtectedRoute>
              <AppShell>
                <AffiliationsPage />
              </AppShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/tracker"
          element={
            <ProtectedRoute>
              <AppShell>
                <TrackerPage />
              </AppShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/compliance/transcript/:rosterId"
          element={
            <ProtectedRoute>
              <AppShell>
                <TranscriptPage />
              </AppShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/compliance"
          element={
            <ProtectedRoute>
              <AppShell>
                <CompliancePage />
              </AppShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/catalog"
          element={
            <ProtectedRoute>
              <AppShell>
                <CatalogPage />
              </AppShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/staff"
          element={
            <ProtectedRoute>
              <AppShell>
                <StaffPage />
              </AppShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/sections/:id"
          element={
            <ProtectedRoute>
              <AppShell>
                <SectionBuilderPage />
              </AppShell>
            </ProtectedRoute>
          }
        />
        {/* Every session the caller can see. Without this the whole
            run-the-class half of the app had no entry point at all. */}
        <Route
          path="/sessions"
          element={
            <ProtectedRoute>
              <AppShell>
                <SessionsPage />
              </AppShell>
            </ProtectedRoute>
          }
        />
        {/* The instructor's verification list — the last link in the QR chain. */}
        <Route
          path="/sessions/:id/roster"
          element={
            <ProtectedRoute>
              <AppShell>
                <SessionRosterPage />
              </AppShell>
            </ProtectedRoute>
          }
        />
        {/* The presenter screen - push items, show anonymous responses. */}
        <Route
          path="/sessions/:id/present"
          element={
            <ProtectedRoute>
              <AppShell>
                <PresenterPage />
              </AppShell>
            </ProtectedRoute>
          }
        />
        {/* The closing packet (M38): agreement, attendance record, roster, transcription. */}
        <Route
          path="/sections/:id/packet"
          element={
            <ProtectedRoute>
              <AppShell>
                <PacketPage />
              </AppShell>
            </ProtectedRoute>
          }
        />
        {/* The Official Attendance Record (H8 §5), printable. */}
        <Route
          path="/sections/:id/record"
          element={
            <ProtectedRoute>
              <AppShell>
                <AttendanceRecordPage />
              </AppShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/sections/:id/enrollments"
          element={
            <ProtectedRoute>
              <AppShell>
                <EnrollmentsPage />
              </AppShell>
            </ProtectedRoute>
          }
        />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

export default App;
